import * as React from 'react';
import { cn } from '../../utils/cn';

export type ButtonVariant =
  | 'primary'
  | 'default'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'link'
  | 'bare';
export type ButtonSize = 'sm' | 'md' | 'default' | 'lg' | 'icon';
export type ButtonDensity = 'page' | 'compact';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  density?: ButtonDensity;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary-500 text-white hover:bg-primary-600',
  default: 'bg-primary-500 text-white hover:bg-primary-600',
  secondary: 'bg-surface-muted text-ink hover:bg-surface-strong',
  outline: 'border border-border bg-surface text-ink hover:bg-surface-strong',
  ghost: 'text-ink-subtle hover:bg-surface-strong',
  destructive: 'bg-danger-600 text-white hover:bg-danger-700',
  link: 'text-primary-500 underline-offset-4 hover:underline',
  bare: '',
};

const pageSizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm h-8',
  md: 'px-4 py-2 text-base h-10',
  default: 'px-4 py-2 text-base h-10',
  lg: 'px-6 py-3 text-lg h-12',
  icon: 'h-10 w-10',
};

const compactSizes: Record<ButtonSize, string> = {
  sm: 'h-9 rounded-md px-3',
  md: 'h-10 px-4 py-2',
  default: 'h-10 px-4 py-2',
  lg: 'h-11 rounded-md px-8',
  icon: 'h-10 w-10',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      density = 'page',
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      className={cn(
        variant === 'bare'
          ? 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:pointer-events-none'
          : 'inline-flex items-center justify-center font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
        variant !== 'bare' &&
          (density === 'page'
            ? 'rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring'
            : 'whitespace-nowrap rounded-md text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'),
        variant !== 'bare' &&
          (variant === 'ghost' && density === 'compact'
            ? 'text-ink hover:bg-surface-strong'
            : variantClasses[variant]),
        variant !== 'bare' &&
          (density === 'page' ? pageSizes[size] : compactSizes[size]),
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </svg>
      )}
      {children}
    </button>
  )
);
Button.displayName = 'Button';

export interface IconButtonProps extends Omit<ButtonProps, 'aria-label'> {
  label: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, size = 'icon', type = 'button', ...props }, ref) => (
    <Button ref={ref} aria-label={label} size={size} type={type} {...props} />
  )
);
IconButton.displayName = 'IconButton';

export default Button;
